import { useState, useRef, useEffect } from 'react';
import { createRoute, Link } from '@tanstack/react-router';
import { MessageSquare, Send } from 'lucide-react';
import { Shell } from '../components/layout/Shell';
import {
  useConversations,
  useConversationMessages,
  useSendMessage,
  useMarkConversationRead,
} from '../hooks/useMessaging';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from '../i18n';
import { Route as rootRoute } from './__root';
import styles from './messages.module.css';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/messages',
  component: MessagesPage,
});

function MessagesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: convData, isLoading } = useConversations();
  const [selectedConvId, setSelectedConvId] = useState<string | null>(null);

  const conversations = convData?.data ?? [];

  // Auto-select first conversation on desktop
  useEffect(() => {
    if (!selectedConvId && conversations.length > 0) {
      setSelectedConvId(conversations[0].id);
    }
  }, [conversations, selectedConvId]);

  const selectedConv = conversations.find((c) => c.id === selectedConvId);

  return (
    <Shell>
      <div className={styles.page}>
        {isLoading ? (
          <div className={styles.loading}>{t('common.loading')}</div>
        ) : conversations.length === 0 ? (
          <div className={styles.empty}>
            <MessageSquare size={48} strokeWidth={1} />
            <div className={styles.emptyTitle}>{t('messages.emptyTitle')}</div>
            <div className={styles.emptyDesc}>{t('messages.emptyDesc')}</div>
          </div>
        ) : (
          <div className={styles.layout}>
            {/* Conversation list */}
            <div className={styles.convList}>
              {conversations.map((conv) => (
                <button
                  key={conv.id}
                  className={`${styles.convItem} ${selectedConvId === conv.id ? styles.convItemActive : ''}`}
                  onClick={() => setSelectedConvId(conv.id)}
                >
                  <div className={styles.convAvatar}>
                    {conv.otherUserDisplayName.slice(0, 2).toUpperCase()}
                  </div>
                  <div className={styles.convInfo}>
                    <div className={styles.convName}>{conv.otherUserDisplayName}</div>
                    <div className={styles.convPreview}>
                      {conv.lastMessage.length > 50
                        ? conv.lastMessage.slice(0, 50) + '...'
                        : conv.lastMessage || t('messages.noMessages')}
                    </div>
                  </div>
                  {conv.unreadCount > 0 && (
                    <span className={styles.unreadBadge}>{conv.unreadCount}</span>
                  )}
                </button>
              ))}
            </div>

            {/* Message thread */}
            <div className={styles.threadArea}>
              {selectedConvId && selectedConv ? (
                <MessageThread
                  conversationId={selectedConvId}
                  otherUserId={selectedConv.otherUserId}
                  otherUserName={selectedConv.otherUserDisplayName}
                  currentUserId={user?.userId ?? ''}
                />
              ) : (
                <div className={styles.threadEmpty}>{t('messages.selectConversation')}</div>
              )}
            </div>
          </div>
        )}
      </div>
    </Shell>
  );
}

function MessageThread({
  conversationId,
  otherUserId,
  otherUserName,
  currentUserId,
}: {
  conversationId: string;
  otherUserId: string;
  otherUserName: string;
  currentUserId: string;
}) {
  const { t } = useTranslation();
  const { data, isLoading } = useConversationMessages(conversationId);
  const sendMessage = useSendMessage();
  const markRead = useMarkConversationRead();
  const [input, setInput] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  // Mark as read when thread opens
  useEffect(() => {
    markRead.mutate(conversationId);
  }, [conversationId]);

  // Auto-scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [data?.data.length]);

  const messages = data?.data ?? [];

  function handleSend() {
    const content = input.trim();
    if (!content) return;
    sendMessage.mutate({ userId: otherUserId, content });
    setInput('');
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <div className={styles.thread}>
      <div className={styles.threadHeader}>
        <Link
          to="/profile/$slug"
          params={{ slug: otherUserId }}
          className={styles.threadAvatar}
          title={otherUserName}
        >
          {otherUserName.slice(0, 2).toUpperCase()}
        </Link>
        <Link
          to="/profile/$slug"
          params={{ slug: otherUserId }}
          className={styles.threadName}
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          {otherUserName}
        </Link>
      </div>
      <div className={styles.messageList}>
        {isLoading ? (
          <div className={styles.loading}>{t('common.loading')}</div>
        ) : messages.length === 0 ? (
          <div className={styles.threadNoMessages}>{t('messages.startConversation')}</div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              className={`${styles.bubble} ${
                msg.senderId === currentUserId ? styles.bubbleSent : styles.bubbleReceived
              }`}
            >
              <div className={styles.bubbleContent}>{msg.content}</div>
              <div className={styles.bubbleTime}>
                {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>
      <div className={styles.inputArea}>
        <textarea
          className={styles.messageInput}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('messages.placeholder')}
          maxLength={5000}
          rows={1}
        />
        <button
          className={styles.sendBtn}
          onClick={handleSend}
          disabled={!input.trim() || sendMessage.isPending}
        >
          <Send size={16} strokeWidth={1.75} />
        </button>
      </div>
    </div>
  );
}
